/**
 * Profile sheet: a player's lifetime record — the badge they wear, how far up the ladder they are,
 * their games and wins, and the last twenty matches that got them there.
 *
 * Same `Sheet` shell as Rules and Settings, because it is reached from the same places and a
 * player should not have to learn a second kind of panel. Everything below the header is read-only,
 * so there is no confirm, no footer and no danger zone.
 *
 * It takes a `userId` and fetches on open rather than reading the store, which is what lets the one
 * component serve both roles: `'me'` from the entry header and the Settings row, and a real id when
 * somebody taps another seat at their table. The fetch is re-run whenever the sheet is opened on a
 * different player, and the result of a stale request is dropped (`alive`), so opening two profiles
 * quickly cannot land the first one's record under the second one's name.
 */
import { type ReactNode, useEffect, useState } from 'react';
import type { FormatKey, ProfileMatch, ProfileView } from '@spades/shared';
import { type ProfileFailure, fetchProfile, profileFailureText } from '../../net/profileApi';
import { useStore } from '../../store/store';
import { Avatar, Sheet } from '../ui';
import { SaveProgressCta } from './Account';
import { LevelBadge, tierOf } from '../ui/LevelBadge';

/** Match formats, as a player names them. Keys come off the wire; an unknown one prints itself. */
const FORMAT_LABEL: Record<FormatKey, string> = {
  quick: 'Quick match',
  classic300: 'Classic 300',
  classic500: 'Classic 500',
  money: 'Money Hand',
};

export interface ProfileSheetProps {
  open: boolean;
  onClose: () => void;
  /** Whose record: `'me'` for the caller's own, a user id for anyone seated at their table. */
  userId?: string | 'me';
}

export function ProfileSheet({ open, onClose, userId = 'me' }: ProfileSheetProps) {
  const setProfile = useStore((s) => s.setProfile);
  const [profile, setLocal] = useState<ProfileView | null>(null);
  const [failure, setFailure] = useState<ProfileFailure | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setFailure(null);
    setLocal(null);
    void fetchProfile(userId).then((res) => {
      if (!alive) return;
      setLoading(false);
      if (res.ok) {
        setLocal(res.profile);
        // Only my own record belongs in the store: it is what the entry header's badge falls back
        // to when there is no table, and caching a stranger's there would put their level on it.
        if (userId === 'me') setProfile(res.profile);
      } else {
        setFailure(res.reason);
      }
    });
    return () => {
      alive = false;
    };
  }, [open, userId, setProfile]);

  return (
    <Sheet open={open} title={userId === 'me' ? 'Your record' : 'Player record'} onClose={onClose} testId="profile">
      {loading ? (
        <p className="profile__notice muted" data-testid="profile-loading">
          Reading the records…
        </p>
      ) : failure ? (
        <p className="profile__notice muted" data-testid="profile-error">
          {profileFailureText(failure)}
        </p>
      ) : profile ? (
        <ProfileBody profile={profile}>
          {/* Own record only, and only for a web guest (the CTA decides that part). */}
          {userId === 'me' && <SaveProgressCta where="profile" level={profile.level} />}
        </ProfileBody>
      ) : null}
    </Sheet>
  );
}

/**
 * The record itself. Pure props, no fetching and no store — so the same body can later hang off a
 * route or a lobby panel, and so a test can render it from a literal.
 */
export function ProfileBody({ profile, children }: { profile: ProfileView; children?: ReactNode }) {
  const name = profile.displayName?.trim() || 'Player';
  const nextTier = tierOf(profile.nextLevel);
  const tierChanges = nextTier.label !== tierOf(profile.level).label;
  const winPct = profile.winrate == null ? null : Math.round(profile.winrate * 100);
  const fill = Math.max(0, Math.min(1, profile.fraction));

  return (
    <div className="stack profile" data-testid="profile-body">
      <header className="profile__head">
        <Avatar name={name} src={profile.avatar} size={54} />
        <div className="profile__ident">
          <div className="display display--md profile__name">{name}</div>
          <div className="eyebrow" data-testid="profile-tier">
            {profile.tierName} · Level {profile.level}
          </div>
        </div>
        <LevelBadge level={profile.level} size={56} glow title={`${profile.tierName} — level ${profile.level}`} />
      </header>

      {/* The ladder. Same numerals the match-end panel shows — points INTO this level over what it
          asks for, never the lifetime total, which means nothing on its own. */}
      <section className="profile__ladder" aria-label="Level progress">
        <div className="profile__bar-row">
          <div className="xp-bar">
            <span className="xp-bar__fill" style={{ transform: `scaleX(${fill})` }} />
          </div>
          {/* The next tier's colour, before it is earned: that is what makes one more level a
              reward rather than a bigger number. Only shown when the next level actually changes
              band — an identical badge one number up says nothing. */}
          {tierChanges && <LevelBadge level={profile.nextLevel} size={30} dim title={`Next tier — ${nextTier.label}`} />}
        </div>
        <p className="profile__numerals tnum" data-testid="profile-xp">
          <span className="profile__now">{profile.intoLevel}</span>
          <span className="profile__sep" aria-hidden>
            /
          </span>
          <span className="muted">{profile.required} XP to level {profile.nextLevel}</span>
        </p>
      </section>

      {/* Under the ladder, where the level it offers to keep was just read. */}
      {children}

      <section className="profile__stats" aria-label="Record">
        <Stat label="Matches" value={String(profile.gamesPlayed)} />
        <Stat label="Wins" value={String(profile.wins)} />
        {/* No matches means no rate — an em dash, never a manufactured 0%. */}
        <Stat label="Winrate" value={winPct == null ? '—' : `${winPct}%`} />
      </section>

      <section className="profile__matches" aria-label="Recent matches">
        <div className="eyebrow">Recent matches</div>
        {profile.matches.length === 0 ? (
          <p className="small muted" style={{ margin: 0 }} data-testid="profile-no-matches">
            Nothing recorded yet.
          </p>
        ) : (
          <div className="profile__list">
            {profile.matches.map((m) => (
              <MatchRow key={`${m.matchId}-${m.seat}`} match={m} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="profile__stat">
      <span className="profile__stat-value tnum">{value}</span>
      <span className="profile__stat-label">{label}</span>
    </div>
  );
}

function MatchRow({ match }: { match: ProfileMatch }) {
  const verdict = match.won == null ? null : match.won;
  return (
    <div className="profile__match" data-testid="profile-match">
      <div className="profile__match-main">
        <span className="profile__match-format">{FORMAT_LABEL[match.format] ?? match.format}</span>
        <span className="profile__match-meta tnum">
          {relativeDate(match.endedAt)} · {match.teamTotal}–{match.opponentTotal}
        </span>
      </div>
      <span className={`profile__verdict${verdict == null ? '' : verdict ? ' profile__verdict--won' : ' profile__verdict--lost'}`}>
        {verdict == null ? 'Unfinished' : verdict ? 'Victory' : 'Defeat'}
      </span>
      <span className="profile__match-xp tnum">+{match.xp}</span>
    </div>
  );
}

/** "12m ago" / "yesterday" / "Mar 4" — short enough to sit beside the format. */
export function relativeDate(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 28) return `${Math.floor(days / 7)}w ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
